import { useCallback, useRef } from 'react';
import { useReorderTask, useUpdateTask } from '../../../../hooks/queries/useTasks';
import { TaskItem } from '../../../../types/topic';
import { logger } from '../../../../utils/logger';
import { useValidTaskStatus } from './useValidTaskStatus';
import { FormattedTaskColumn } from '../../../../hooks/queries/taskColumnTypes';

/**
 * Hook for task moving and reordering functions
 * Migrated to use React Query instead of deprecated taskStore
 */
export function useTaskMove(topicId: string, tasks: TaskItem[], columns: FormattedTaskColumn[]) {
  // React Query mutations - the modern way to handle task operations
  const reorderTaskMutation = useReorderTask();
  const updateTaskMutation = useUpdateTask();
  
  // Use the validation hook to ensure we only use valid status values
  const { getSafeStatus, isValidStatus } = useValidTaskStatus(topicId);

  // Last task movement timestamp - used for debouncing (using refs to ensure persistence between renders)
  const lastMoveTimestampRef = useRef<number>(0);
  const DEBOUNCE_THRESHOLD_MS = 300; // Prevent multiple reorders within 300ms

  // Function to handle task reordering within the same column
  const moveTask = useCallback(
    async (id: string, toIndex: number) => {
      // Debounce protection - prevent multiple rapid reordering operations
      const now = Date.now();
      if (now - lastMoveTimestampRef.current < DEBOUNCE_THRESHOLD_MS) {
        logger.component('Ignoring rapid task move - debounced', {
          taskId: id.slice(0, 8),
          timeSinceLastMove: now - lastMoveTimestampRef.current,
          threshold: DEBOUNCE_THRESHOLD_MS
        });
        return;
      }
      lastMoveTimestampRef.current = now;
      
      // Ensure toIndex is never negative
      const safeToIndex = Math.max(0, toIndex);
      
      // Find the task to move
      const taskToMove = tasks.find(t => t.id === id);
      if (!taskToMove) {
        logger.error('Task not found for reordering:', id);
        return;
      }

      // Get all tasks in the same column
      const tasksInColumn = tasks
        .filter(t => t.status === taskToMove.status)
        .sort((a, b) => (a.position || 0) - (b.position || 0));

      const fromIndex = tasksInColumn.findIndex(t => t.id === id);
      
      // If the task is already at the target position, do nothing
      if (fromIndex === safeToIndex) {
        logger.component('Task is already at the target position. No changes needed.');
        return;
      }

      logger.component('===== TASK REORDERING DEBUG =====');
      logger.component(`Moving task "${taskToMove.title}" (ID: ${id.slice(0, 8)}...)`);
      logger.component(`From index ${fromIndex} to index ${safeToIndex}`);

      try {
        // Use React Query mutation for reordering - it handles optimistic updates automatically
        await reorderTaskMutation.mutateAsync({
          topicId,
          taskId: id,
          newStatus: taskToMove.status, // Keep same status since this is within column
          newIndex: safeToIndex
        });

        logger.component('Task reordering completed successfully');
      } catch (error) {
        logger.error('Failed to reorder task:', {
          error: error instanceof Error ? error.message : String(error),
          taskId: id,
          toIndex: safeToIndex,
        });
      }
    },
    [topicId, tasks, reorderTaskMutation]
  );

  // Last cross-column movement timestamp - used for debouncing (using refs to ensure persistence between renders)
  const lastCrossColumnMoveTimestampRef = useRef<number>(0);
  const CROSS_COLUMN_DEBOUNCE_MS = 300; // Prevent multiple moves within 300ms

  // Move task between columns with optimistic updates
  const moveTaskBetweenColumns = useCallback(
    async (id: string, newStatus: string, newIndex: number = 0) => {
      // Always force index to 0 for cross-column movements to ensure
      // tasks go to the top of the target column
      if (newIndex !== 0) {
        logger.component('Forcing cross-column move to position 0', {
          requestedIndex: newIndex,
          taskId: id.slice(0, 8)
        });
        newIndex = 0;
      }
      
      // Debounce protection for cross-column movements
      const now = Date.now();
      if (now - lastCrossColumnMoveTimestampRef.current < CROSS_COLUMN_DEBOUNCE_MS) {
        logger.component('Ignoring rapid cross-column move - debounced', {
          taskId: id.slice(0, 8),
          timeSinceLastMove: now - lastCrossColumnMoveTimestampRef.current,
          threshold: CROSS_COLUMN_DEBOUNCE_MS
        });
        return;
      }
      lastCrossColumnMoveTimestampRef.current = now;
      
      // First, ensure the status is valid to prevent constraint violations
      if (!isValidStatus(newStatus)) {
        logger.error('Prevented move to invalid status', {
          attemptedStatus: newStatus,
          taskId: id,
        });
        return;
      }

      // Get the validated status
      const safeStatus = getSafeStatus(newStatus);

      // Find the task being moved
      const taskToMove = tasks.find(t => t.id === id);
      if (!taskToMove) {
        logger.error('Task not found for column move', { taskId: id });
        return;
      }

      // Check if task is already in the target column - no need to move
      if (taskToMove.status === safeStatus) {
        logger.component('Task is already in the target column. No column move needed.', {
          taskId: id,
          status: safeStatus,
        });
        return;
      }

      // Verify target column exists
      const targetColumn = columns.find(c => c.status === safeStatus);
      if (!targetColumn) {
        logger.error('Target column not found for move', {
          status: safeStatus,
          originalStatus: newStatus,
          isStatusValid: isValidStatus(newStatus),
        });
        return;
      }

      // Find source column
      const sourceColumn = columns.find(c => c.status === taskToMove.status);

      // Only skip if it's the EXACT same column (same ID)
      if (sourceColumn && sourceColumn.id === targetColumn.id) {
        return;
      }

      // Log detailed information about the task move
      logger.component('Moving task between columns', {
        taskId: id,
        taskTitle: taskToMove.title,
        fromStatus: taskToMove.status,
        toStatus: safeStatus,
        currentPosition: taskToMove.position,
        newIndex: newIndex
      });

      try {
        // For cross-column moves, we need to check if it's just a status change or a full reorder
        if (newIndex === 0) {
          // Simple status change to top of column - use updateTask mutation
          await updateTaskMutation.mutateAsync({
            topicId,
            taskId: id,
            updates: { status: safeStatus }
          });
        } else {
          // Full reorder with position change - use reorderTask mutation
          await reorderTaskMutation.mutateAsync({
            topicId,
            taskId: id,
            newStatus: safeStatus,
            newIndex
          });
        }

        logger.component('Cross-column task move completed successfully');
      } catch (error) {
        logger.error('Failed to move task between columns:', {
          error: error instanceof Error ? error.message : String(error),
          taskId: id,
          fromStatus: taskToMove.status,
          toStatus: safeStatus,
          newIndex,
        });
      }
    },
    [columns, topicId, tasks, isValidStatus, getSafeStatus, updateTaskMutation, reorderTaskMutation]
  );

  // Function to directly move tasks to empty columns
  const forceTaskMove = useCallback(
    async (taskId: string, targetColumnStatus: string) => {
      // First verify that the status is valid
      if (!isValidStatus(targetColumnStatus)) {
        logger.error('Attempted to move task to invalid status', {
          taskId,
          attemptedStatus: targetColumnStatus,
        });
        return;
      }

      // Get the safe, validated status
      const safeStatus = getSafeStatus(targetColumnStatus);

      // Find the task directly
      const taskToMove = tasks.find(t => t.id === taskId);
      if (!taskToMove) {
        logger.error('Task not found for force move', { taskId });
        return;
      }

      // Verify target column exists
      const targetColumn = columns.find(c => c.status === safeStatus);
      if (!targetColumn) {
        logger.error('Target column not found for force move', { 
          taskId,
          targetStatus: safeStatus 
        });
        return;
      }

      try {
        // Use React Query mutation to update task status
        await updateTaskMutation.mutateAsync({
          topicId,
          taskId,
          updates: { status: safeStatus }
        });

        logger.component('Force task move completed successfully', {
          taskId,
          newStatus: safeStatus,
        });
      } catch (error) {
        logger.error('Failed to perform manual task move', {
          error: error instanceof Error ? error.message : String(error),
          taskId,
          status: safeStatus,
        });
      }
    },
    [columns, tasks, topicId, isValidStatus, getSafeStatus, updateTaskMutation]
  );

  return {
    moveTask,
    moveTaskBetweenColumns,
    forceTaskMove,
  };
}
